import React, { useState, useEffect } from 'react';
import { useToast } from '../contexts/ToastContext';
import { getClients, saveClient, updateClient, deleteClient } from '../services/apiService';

function ClientsPage() {
  const { showToast } = useToast();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  
  // Form state
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  
  useEffect(() => {
    loadClients();
  }, []);
  
  const loadClients = async () => {
    try {
      setLoading(true);
      const response = await getClients();
      
      if (response && response.success && Array.isArray(response.result)) {
        setClients(response.result);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Failed to load clients:', error);
      setError(error.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  };
  
  const openClientModal = (client = null) => {
    if (client) {
      setEditingClient(client);
      setClientName(client.name);
      setClientEmail(client.email || '');
      setClientPhone(client.phone || '');
    } else {
      setEditingClient(null);
      setClientName('');
      setClientEmail('');
      setClientPhone('');
    }
    setShowModal(true);
  };
  
  const closeModal = () => {
    setShowModal(false);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const clientData = {
      name: clientName,
      email: clientEmail,
      phone: clientPhone
    };
    
    try {
      if (editingClient) {
        // Update existing client
        await updateClient(editingClient.id, clientData);
        showToast('Client updated successfully!', 'success');
      } else {
        // Create new client
        await saveClient(clientData);
        showToast('Client created successfully!', 'success');
      }
      
      closeModal();
      loadClients();
    } catch (error) {
      showToast('Failed to save client: ' + error.message, 'error');
    }
  };
  
  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) {
      return;
    }
    
    try {
      await deleteClient(clientId);
      showToast('Client deleted successfully!', 'success');
      loadClients();
    } catch (error) {
      showToast('Failed to delete client: ' + error.message, 'error');
    }
  };
  
  if (loading) {
    return (
      <div className="loading-spinner">
        <i className="fas fa-spinner fa-spin"></i> Loading clients...
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="error-state">
        <i className="fas fa-exclamation-circle"></i>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={loadClients}>
          Try Again
        </button>
      </div>
    );
  }
  
  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Clients</h1>
        <p className="page-description">Manage your clients</p>
      </div>
      
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">All Clients</h2>
          <button className="btn btn-primary" onClick={() => openClientModal()}>
            <i className="fas fa-plus"></i> Add New Client
          </button>
        </div>
        <div className="card-body">
          {clients.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-users"></i>
              <p>No clients found</p>
              <p className="empty-state-description">Add your first client to get started</p>
              <button className="btn btn-primary" onClick={() => openClientModal()}>
                Add Client
              </button>
            </div>
          ) : (
            <div className="clients-grid">
              {clients.map(client => (
                <div key={client.id} className="client-card">
                  <div className="client-header">
                    <span>{client.name}</span>
                  </div>
                  <div className="client-body">
                    <div className="client-info">
                      {client.email && (
                        <div className="client-info-item">
                          <span className="client-info-label">Email:</span>
                          <span>{client.email}</span>
                        </div>
                      )}
                      {client.phone && (
                        <div className="client-info-item">
                          <span className="client-info-label">Phone:</span>
                          <span>{client.phone}</span>
                        </div>
                      )}
                    </div>
                    <div className="client-actions">
                      <button 
                        className="btn btn-secondary btn-sm" 
                        onClick={() => openClientModal(client)}
                      >
                        <i className="fas fa-edit"></i> Edit
                      </button>
                      <button 
                        className="btn btn-danger btn-sm" 
                        onClick={() => handleDeleteClient(client.id)}
                      >
                        <i className="fas fa-trash"></i> Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Client Modal */}
      {showModal && (
        <div className="modal-backdrop">
          <div className="modal">
            <div className="modal-content">
              <div className="modal-header">
                <h2>{editingClient ? 'Edit Client' : 'Add New Client'}</h2>
                <button className="modal-close" onClick={closeModal}>&times;</button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="clientName">Client Name</label>
                    <input 
                      type="text" 
                      id="clientName" 
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      placeholder="Enter client name" 
                      required 
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="clientEmail">Email</label>
                    <input 
                      type="email" 
                      id="clientEmail" 
                      value={clientEmail}
                      onChange={(e) => setClientEmail(e.target.value)}
                      placeholder="Enter client email" 
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="clientPhone">Phone</label>
                    <input 
                      type="tel" 
                      id="clientPhone" 
                      value={clientPhone}
                      onChange={(e) => setClientPhone(e.target.value)}
                      placeholder="Enter client phone" 
                    />
                  </div>
                  
                  <div className="form-actions">
                    <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                    <button type="submit" className="btn btn-primary">
                      {editingClient ? 'Update Client' : 'Add Client'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientsPage; 